import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Globe, ExternalLink, Copy, Check, Image, Type, Info, ChevronDown, ChevronUp, Smartphone, Monitor, Code, Camera, Award, MapPin, Briefcase, BarChart, MessageCircle, Clipboard, Zap, Palette, CheckCircle } from 'lucide-react';
import ColorThief from 'colorthief';
import '../styles/BrandfetchDisplay.css';
import LoadingSpinner from './LoadingSpinner';

/**
 * An enhanced component to display comprehensive brand information optimized for iPad viewing
 * @param {Object} props - Component props
 * @param {string} props.domain - The domain to fetch data for
 * @param {Function} props.onClose - Function to call when closing the display
 * @param {Function} props.onSwitchView - Function to call when switching to single page view
 */
const BrandfetchDisplay = ({ domain, onClose, onSwitchView }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [domainData, setDomainData] = useState(null);
    const [aiData, setAiData] = useState(null);
    const [brandData, setBrandData] = useState(null);
    const [dominantColors, setDominantColors] = useState(['#f0f0f0']);
    const [activeSection, setActiveSection] = useState('overview');
    const [copiedColor, setCopiedColor] = useState(null);
    const [appIdeas, setAppIdeas] = useState([]);
    const [features, setFeatures] = useState([]);
    const [marketingTips, setMarketingTips] = useState([]);
    const [contentCategories, setContentCategories] = useState([]);
    const [brandAnalysis, setBrandAnalysis] = useState(null);
    const [colorSchemes, setColorSchemes] = useState([]);
    const [socialLinksWithOG, setSocialLinksWithOG] = useState([]);
    const [appSuggestions, setAppSuggestions] = useState(null);
    const [technicalRequirements, setTechnicalRequirements] = useState([]);

    // Extract social links with useMemo to ensure it's defined before the useEffect that uses it
    const socialLinks = useMemo(() => {
        return brandData?.links?.map(link => ({
            type: link.name || 'website',
            url: link.url,
            username: link.username || null,
            followers: link.followers || null,
            verified: link.verified || false
        })) || [];
    }, [brandData]);

    // Define appLinks and industries using useMemo as well
    const appLinks = useMemo(() => brandData?.appLinks || [], [brandData]);
    const industries = useMemo(() => brandData?.industries || [], [brandData]);
    const companyInfo = useMemo(() => brandData?.analysis || {}, [brandData]);
    const screenshots = useMemo(() => brandData?.screenshots || {}, [brandData]);

    // Helper function to determine text color based on background
    const getTextColor = (bgColor) => {
        if (!bgColor) return '#000000';
        const rgb = bgColor.match(/\d+/g);
        if (!rgb) return '#000000';
        const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
        return luminance > 0.5 ? '#000000' : '#ffffff';
    };

    useEffect(() => {
        if (domain) {
            fetchAllData();
        }
    }, [domain]);

    // Add color extraction from logo with better error handling
    useEffect(() => {
        const extractColors = async () => {
            if (!brandData?.logos?.length || !brandData.logos[0]?.formats?.length) {
                // Use default colors if no logo available
                setDominantColors(['#f0f0f0', '#e0e0e0', '#d0d0d0']);
                return;
            }
            
            try {
                const logoUrl = brandData.logos[0].formats[0].src;
                
                // Create a new image element using document.createElement
                const img = document.createElement('img');
                img.crossOrigin = 'Anonymous';
                
                // Wrap in a promise to handle async image loading
                await new Promise((resolve, reject) => {
                    img.onload = () => {
                        try {
                            const colorThief = new ColorThief();
                            const palette = colorThief.getPalette(img, 3);
                            
                            // Convert RGB arrays to CSS color strings
                            const colorStrings = palette.map(color => 
                                `rgb(${color[0]}, ${color[1]}, ${color[2]})`
                            );
                            
                            setDominantColors(colorStrings);
                            resolve();
                        } catch (error) {
                            console.error('Error extracting colors:', error);
                            // Use fallback colors
                            setDominantColors(['#f0f0f0', '#e0e0e0', '#d0d0d0']);
                            resolve(); // Resolve anyway to continue
                        }
                    };
                    
                    img.onerror = () => {
                        console.error('Error loading image for color extraction');
                        // Use fallback colors
                        setDominantColors(['#f0f0f0', '#e0e0e0', '#d0d0d0']);
                        resolve(); // Resolve anyway to continue
                    };
                    
                    // Set the src to start loading
                    img.src = logoUrl;
                });
            } catch (error) {
                console.error('Error in color extraction:', error);
                setDominantColors(['#f0f0f0', '#e0e0e0', '#d0d0d0']);
            }
        };
        
        extractColors();
    }, [brandData]);

    // Function to handle refresh button click
    const handleRefresh = () => {
        fetchAllData(true);
    };

    const fetchAllData = async (refresh = false) => {
        try {
            setLoading(true);
            setError(null);
            
            // Flag to prevent repeated requests for ai-data
            const aiDataChecked = {value: false};
            
            // Normalize domain
            const normalizedDomain = domain.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0];
            
            // Fetch brand data first
            const brandResponse = await fetch(`/api/website-data/brandfetch/${normalizedDomain}${refresh ? '?refresh=true' : ''}`);
            let brandResult = null;
            
            if (brandResponse.ok) {
                brandResult = await brandResponse.json();
            }
            
            // Prepare safe fetch function to handle 404 and other errors
            const safeFetch = async (url) => {
                try {
                    // Don't attempt to fetch AI data if it's known to be missing
                    if (url.includes('/ai-data') && aiDataChecked.value) {
                        return null;
                    }
                    
                    const response = await fetch(url);
                    if (response.ok) {
                        if (url.includes('/ai-data')) {
                            aiDataChecked.value = true;
                        }
                        return await response.json();
                    }
                    
                    // Only log as a warning for 404s on optional data endpoints
                    if (response.status === 404) {
                        if (url.includes('/ai-data')) {
                            // Set flag to avoid repeated requests for ai-data
                            aiDataChecked.value = true;
                        }
                        
                        // These are optional endpoints, so return null silently without logging
                        return null;
                    }
                    
                    console.warn(`Failed to fetch ${url}: ${response.status}`);
                    return null;
                } catch (err) {
                    // Suppress error logging for known optional endpoints
                    if (!url.includes('/ai-data') && 
                        !url.includes('/app-ideas') && 
                        !url.includes('/features') && 
                        !url.includes('/color-schemes') && 
                        !url.includes('/marketing-tips') && 
                        !url.includes('/content-categories') && 
                        !url.includes('/brand-analysis')) {
                        console.error(`Error fetching ${url}:`, err);
                    }
                    return null;
                }
            };
            
            // Fetch additional data with safe fetch
            const domainResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}`);
            const aiDataResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}/ai-data`);
            const appIdeasResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}/app-ideas`);
            const featuresResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}/features`);
            const colorSchemesResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}/color-schemes`);
            const marketingTipsResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}/marketing-tips`);
            const contentCategoriesResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}/content-categories`);
            const brandAnalysisResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}/brand-analysis`);
            const appSuggestionsResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}/app-suggestions`);
            const technicalRequirementsResult = aiDataResult?.technicalRequirements || [];
            
            // Set state with results
            setBrandData(brandResult);
            setDomainData(domainResult);
            // API might return aiData with a nested data property, or directly contain the data
            // Handle both structures
            if (aiDataResult) {
                const processedAiData = aiDataResult.data ? aiDataResult.data : aiDataResult;
                
                // Process insights to ensure they're safe to render
                if (processedAiData && processedAiData.insights) {
                    if (Array.isArray(processedAiData.insights)) {
                        processedAiData.insights = processedAiData.insights.map(insight => {
                            return typeof insight === 'object' ? JSON.stringify(insight) : insight;
                        });
                    } else if (typeof processedAiData.insights === 'object') {
                        // Convert each insight value to string if it's an object
                        Object.keys(processedAiData.insights).forEach(key => {
                            const value = processedAiData.insights[key];
                            if (typeof value === 'object' && value !== null) {
                                processedAiData.insights[key] = JSON.stringify(value);
                            }
                        });
                    }
                }
                
                // Process recommendations similarly
                if (processedAiData && processedAiData.recommendations && Array.isArray(processedAiData.recommendations)) {
                    processedAiData.recommendations = processedAiData.recommendations.map(rec => {
                        return typeof rec === 'object' ? JSON.stringify(rec) : rec;
                    });
                }
                
                setAiData(processedAiData);
            } else {
                setAiData(null);
            }
            setAppIdeas(appIdeasResult || []);
            setFeatures(featuresResult || []);
            setColorSchemes(colorSchemesResult || []);
            setMarketingTips(marketingTipsResult || []);
            
            // Process contentCategories to make sure they're safe to render
            if (contentCategoriesResult && Array.isArray(contentCategoriesResult)) {
                const safeCategories = contentCategoriesResult.map(category => {
                    if (typeof category === 'string') {
                        return {
                            name: category,
                            description: `Content related to ${category}`
                        };
                    } else if (category && typeof category === 'object') {
                        return {
                            name: ensureString(category.name || 'Category'),
                            description: ensureString(category.description || '')
                        };
                    } else {
                        return {
                            name: 'Unknown Category',
                            description: ''
                        };
                    }
                });
                setContentCategories(safeCategories);
            } else {
                setContentCategories([]);
            }
            
            setBrandAnalysis(brandAnalysisResult || null);
            setAppSuggestions(appSuggestionsResult || null);
            setTechnicalRequirements(technicalRequirementsResult);
            
            // Set loading to false
            setLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Failed to load brand information. Please try again.');
            setLoading(false);
        }
    };

    // Make the ensureString function more robust
    const ensureString = (value) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'number') return value.toString();
        if (typeof value === 'boolean') return value.toString();
        if (Array.isArray(value)) return JSON.stringify(value);
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch (e) {
                return '[Object]';
            }
        }
        return String(value);
    };

    // Helper to safely process any data that might be an object
    const safelyProcessObject = (obj, defaultValue = {}) => {
        if (!obj || typeof obj !== 'object') {
            return defaultValue;
        }
        
        // Create a new object with safely processed values
        const result = {};
        Object.keys(obj).forEach(key => {
            const value = obj[key];
            if (typeof value === 'object' && value !== null) {
                // If the value is an object, stringify it
                result[key] = JSON.stringify(value);
            } else {
                // Otherwise, pass through
                result[key] = value;
            }
        });
        
        return result;
    };

    // Fetch OpenGraph data for social links
    useEffect(() => {
        const fetchOpenGraphData = async () => {
            if (!socialLinks || socialLinks.length === 0) return;
            
            const linksWithOG = [...socialLinks];
            
            for (let i = 0; i < linksWithOG.length; i++) {
                const link = linksWithOG[i];
                if (!link.url) continue;
                
                try {
                    const response = await axios.get(`/api/website-data/domain/${domain}/opengraph?url=${encodeURIComponent(link.url)}`);
                    linksWithOG[i] = {
                        ...link,
                        og: response.data
                    };
                } catch (error) {
                    console.error('Error fetching OpenGraph data:', error);
                }
            }
            
            setSocialLinksWithOG(linksWithOG);
        };
        
        fetchOpenGraphData();
    }, [domain, socialLinks]);

    if (loading) {
        return (
            <div className="brandfetch-modal" onClick={(e) => {
                e.stopPropagation();
                if (onClose) onClose();
            }}>
                <div className="brandfetch-modal-content loading-container" onClick={(e) => e.stopPropagation()}>
                    {/* Removing the close button as requested */}
                    <h2 className="modal-title">Loading Brand Information</h2>
                    <LoadingSpinner />
                    <p className="loading-text">Retrieving data for {domain}...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="brandfetch-modal" onClick={(e) => {
                e.stopPropagation();
                if (onClose) onClose();
            }}>
                <div className="brandfetch-modal-content error-container" onClick={(e) => e.stopPropagation()}>
                    {/* Removing the close button as requested */}
                    <h2 className="modal-title">Error</h2>
                    <div className="error-icon">⚠️</div>
                    <p>{error}</p>
                    <button className="btn btn-primary" onClick={handleRefresh}>Try Again</button>
                </div>
            </div>
        );
    }

    if (!brandData && !domainData) {
        return (
            <div className="brandfetch-modal" onClick={(e) => {
                e.stopPropagation();
                if (onClose) onClose();
            }}>
                <div className="brandfetch-modal-content error-container" onClick={(e) => e.stopPropagation()}>
                    {/* Removing the close button as requested */}
                    <h2 className="modal-title">No Information Available</h2>
                    <div className="error-icon">⚠️</div>
                    <p>We couldn't find any information for {domain}.</p>
                    <button className="btn btn-primary" onClick={handleRefresh}>Try Again</button>
                </div>
            </div>
        );
    }

    const { name, description } = brandData || {};
    const { websiteData, colorSchemes: domainColorSchemes } = domainData || {};
    const { insights = {}, recommendations = [] } = aiData || {};
    
    // Generate gradient background style
    const gradientStyle = dominantColors.length > 0 ? {
        background: `linear-gradient(135deg, ${dominantColors.join(', ')})`,
        color: getTextColor(dominantColors[0])
    } : {};

    return (
        <div className="brandfetch-modal" onClick={(e) => {
            e.stopPropagation();
            if (onClose) onClose();
        }}>
            <div className="brandfetch-modal-content" style={{height: '90vh', display: 'flex', flexDirection: 'column'}} onClick={(e) => e.stopPropagation()}>
                {/* Removing the close button as requested */}
                
                <h2 className="modal-title">Brand Information: {brandData?.name || domain}</h2>
                
                {/* Add view toggle button */}
                {onSwitchView && (
                    <button 
                        className="view-toggle-btn" 
                        onClick={(e) => {
                            e.stopPropagation();
                            onSwitchView();
                        }}
                        style={{
                            position: 'absolute',
                            top: '15px',
                            right: '15px',
                            background: '#f0f0f0',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '13px'
                        }}
                    >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            <Monitor size={14} />
                        </span>
                        Switch to Single Page View
                    </button>
                )}
                
                {/* Header section with logo and brand name */}
                <div className="brand-header" style={gradientStyle}>
                    <div className="brand-header-backdrop" style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                    }}></div>
                    {brandData?.logos?.[0]?.formats?.[0]?.src && (
                        <div className="brand-logo" style={{
                            position: 'relative',
                            zIndex: 2,
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '10px',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                            maxWidth: '120px',
                            maxHeight: '120px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '-20px 20px 0 0'
                        }}>
                            <img 
                                src={brandData.logos[0].formats[0].src} 
                                alt={`${brandData?.name || domain} logo`}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                    )}
                    <div className="brand-title" style={{
                        position: 'relative',
                        zIndex: 2
                    }}>
                        <h3>{brandData?.name || aiData?.company_name || domain}</h3>
                        <div className="brand-meta">
                            <a 
                                href={`https://${domain}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="domain-link"
                            >
                                {domain} ↗️
                            </a>
                            {brandData?.company?.location?.country && (
                                <span className="country-tag">
                                    {brandData.company.location.country}
                                </span>
                            )}
                            <button onClick={handleRefresh} className="refresh-button">
                                ↻ Refresh Data
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Navigation Tabs */}
                <div className="navigation-tabs">
                    <button 
                        className={`tab ${activeSection === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveSection('overview')}
                    >
                        Overview
                    </button>
                    <button 
                        className={`tab ${activeSection === 'brand' ? 'active' : ''}`}
                        onClick={() => setActiveSection('brand')}
                    >
                        Brand Assets
                    </button>
                    <button 
                        className={`tab ${activeSection === 'insights' ? 'active' : ''}`}
                        onClick={() => setActiveSection('insights')}
                    >
                        AI Insights
                    </button>
                    <button 
                        className={`tab ${activeSection === 'media' ? 'active' : ''}`}
                        onClick={() => setActiveSection('media')}
                    >
                        Media & Images
                    </button>
                    <button 
                        className={`tab ${activeSection === 'links' ? 'active' : ''}`}
                        onClick={() => setActiveSection('links')}
                    >
                        Links & Social
                    </button>
                    <button 
                        className={`tab ${activeSection === 'ideas' ? 'active' : ''}`}
                        onClick={() => setActiveSection('ideas')}
                    >
                        App Ideas
                    </button>
                    <button 
                        className={`tab ${activeSection === 'marketing' ? 'active' : ''}`}
                        onClick={() => setActiveSection('marketing')}
                    >
                        Marketing
                    </button>
                </div>
                
                {/* Content Container with fixed height */}
                <div className="content-container" style={{
                    flex: 1, 
                    overflowY: 'auto',
                    backgroundColor: 'rgba(255, 255, 255, 0.65)',
                    backdropFilter: 'blur(7px)',
                    WebkitBackdropFilter: 'blur(7px)',
                    borderRadius: '8px',
                    margin: '1rem 0',
                    padding: '1.5rem',
                    boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.1)'
                }}>
                    {activeSection === 'overview' && (
                        <div className="section overview-section">
                            {/* Description */}
                            <div className="info-card" style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                marginBottom: '1.5rem',
                                borderRadius: '10px',
                                border: '1px solid rgba(255, 255, 255, 0.2)'
                            }}>
                                <h3>Description</h3>
                                <p>{description || 'No description available.'}</p>
                            </div>

                            {/* Brand Analysis */}
                            {brandAnalysis && Object.keys(brandAnalysis).length > 0 ? (
                                <div className="info-card" style={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    marginBottom: '1.5rem',
                                    borderRadius: '10px',
                                    border: '1px solid rgba(255, 255, 255, 0.2)'
                                }}>
                                    <h3>Brand Analysis</h3>
                                    <div className="brand-analysis-content" style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                        gap: '1rem'
                                    }}>
                                        {brandAnalysis.industry_category && (
                                            <div className="analysis-item" style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                                padding: '1rem',
                                                borderRadius: '8px'
                                            }}>
                                                <h4 style={{ color: dominantColors[0] || '#333', marginTop: 0 }}>Industry</h4>
                                                <p>{brandAnalysis.industry_category}</p>
                                            </div>
                                        )}
                                        {brandAnalysis.target_audience && (
                                            <div className="analysis-item" style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                                padding: '1rem',
                                                borderRadius: '8px'
                                            }}>
                                                <h4 style={{ color: dominantColors[0] || '#333', marginTop: 0 }}>Target Audience</h4>
                                                <p>{brandAnalysis.target_audience}</p>
                                            </div>
                                        )}
                                        {brandAnalysis.brand_voice && (
                                            <div className="analysis-item" style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                                padding: '1rem',
                                                borderRadius: '8px'
                                            }}>
                                                <h4 style={{ color: dominantColors[0] || '#333', marginTop: 0 }}>Brand Voice</h4>
                                                <p>{brandAnalysis.brand_voice}</p>
                                            </div>
                                        )}
                                        {brandAnalysis.key_value_propositions && (
                                            <div className="analysis-item" style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                                padding: '1rem',
                                                borderRadius: '8px'
                                            }}>
                                                <h4 style={{ color: dominantColors[0] || '#333', marginTop: 0 }}>Value Propositions</h4>
                                                <p>{brandAnalysis.key_value_propositions}</p>
                                            </div>
                                        )}
                                        {brandAnalysis.market_positioning && (
                                            <div className="analysis-item" style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                                padding: '1rem',
                                                borderRadius: '8px'
                                            }}>
                                                <h4 style={{ color: dominantColors[0] || '#333', marginTop: 0 }}>Market Positioning</h4>
                                                <p>{brandAnalysis.market_positioning}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="info-card" style={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    marginBottom: '1.5rem',
                                    borderRadius: '10px',
                                    border: '1px solid rgba(255, 255, 255, 0.2)'
                                }}>
                                    <h3>Brand Analysis</h3>
                                    <div className="empty-state" style={{
                                        textAlign: 'center',
                                        padding: '2rem',
                                        color: '#666'
                                    }}>
                                        <p>No brand analysis available for this domain.</p>
                                    </div>
                                </div>
                            )}
                            
                            {/* Company Information */}
                            {brandData?.company && Object.keys(brandData.company).length > 0 ? (
                                <div className="info-card company-card" style={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    marginBottom: '1.5rem',
                                    borderRadius: '10px',
                                    border: '1px solid rgba(255, 255, 255, 0.2)'
                                }}>
                                    <h3>Company Information</h3>
                                    <div className="company-info" style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                        gap: '1rem'
                                    }}>
                                        {brandData.company.name && (
                                            <div className="info-item" style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                                padding: '1rem',
                                                borderRadius: '8px'
                                            }}>
                                                <span className="info-label" style={{
                                                    fontWeight: 'bold',
                                                    display: 'block',
                                                    marginBottom: '0.5rem',
                                                    color: dominantColors[0] || '#333'
                                                }}>Company Name</span>
                                                <span className="info-value">{brandData.company.name}</span>
                                            </div>
                                        )}
                                        {brandData.company.description && (
                                            <div className="info-item" style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                                padding: '1rem',
                                                borderRadius: '8px',
                                                gridColumn: brandData.company.name ? 'span 2' : 'auto'
                                            }}>
                                                <span className="info-label" style={{
                                                    fontWeight: 'bold',
                                                    display: 'block',
                                                    marginBottom: '0.5rem',
                                                    color: dominantColors[0] || '#333'
                                                }}>Description</span>
                                                <span className="info-value">{brandData.company.description}</span>
                                            </div>
                                        )}
                                        {brandData.company.founded && (
                                            <div className="info-item" style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                                padding: '1rem',
                                                borderRadius: '8px'
                                            }}>
                                                <span className="info-label" style={{
                                                    fontWeight: 'bold',
                                                    display: 'block',
                                                    marginBottom: '0.5rem',
                                                    color: dominantColors[0] || '#333'
                                                }}>Founded</span>
                                                <span className="info-value">{brandData.company.founded}</span>
                                            </div>
                                        )}
                                        {brandData.company.employees && (
                                            <div className="info-item" style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                                padding: '1rem',
                                                borderRadius: '8px'
                                            }}>
                                                <span className="info-label" style={{
                                                    fontWeight: 'bold',
                                                    display: 'block',
                                                    marginBottom: '0.5rem',
                                                    color: dominantColors[0] || '#333'
                                                }}>Employees</span>
                                                <span className="info-value">{brandData.company.employees}</span>
                                            </div>
                                        )}
                                        {brandData.company.type && (
                                            <div className="info-item" style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                                padding: '1rem',
                                                borderRadius: '8px'
                                            }}>
                                                <span className="info-label" style={{
                                                    fontWeight: 'bold',
                                                    display: 'block',
                                                    marginBottom: '0.5rem',
                                                    color: dominantColors[0] || '#333'
                                                }}>Company Type</span>
                                                <span className="info-value">{brandData.company.type}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="info-card company-card" style={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                    marginBottom: '1.5rem',
                                    borderRadius: '10px',
                                    border: '1px solid rgba(255, 255, 255, 0.2)'
                                }}>
                                    <h3>Company Information</h3>
                                    <div className="empty-state" style={{
                                        textAlign: 'center',
                                        padding: '2rem',
                                        color: '#666'
                                    }}>
                                        <p>No company information available for this domain.</p>
                                    </div>
                                </div>
                            )}
                            
                            {/* Industries */}
                            {industries && industries.length > 0 && (
                                <div className="info-card">
                                    <h3>Industries</h3>
                                    <div className="industries-grid">
                                        {industries.map((industry, index) => (
                                            <div key={index} className="industry-item">
                                                <span className="industry-emoji">{industry.industry_emoji || '🏢'}</span>
                                                <span className="industry-name">{industry.industry_name}</span>
                                                {industry.is_primary && <span className="industry-badge">Primary</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {contentCategories && contentCategories.length > 0 ? (
                                <div className="info-card">
                                    <h3>Content Categories</h3>
                                    <div className="content-categories-grid">
                                        {contentCategories.map((category, index) => (
                                            <div key={index} className="category-item" style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                                backdropFilter: 'blur(5px)',
                                                WebkitBackdropFilter: 'blur(5px)',
                                                padding: '1rem',
                                                borderRadius: '8px',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                                                border: '1px solid rgba(0, 0, 0, 0.05)',
                                                marginBottom: '1rem'
                                            }}>
                                                <span className="category-name" style={{
                                                    fontSize: '1.1rem',
                                                    fontWeight: 'bold',
                                                    display: 'block',
                                                    marginBottom: '0.5rem',
                                                    color: dominantColors[0] || '#333'
                                                }}>{category.name}</span>
                                                <p className="category-description" style={{
                                                    margin: 0,
                                                    fontSize: '0.9rem',
                                                    color: '#555'
                                                }}>{category.description}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="info-card">
                                    <h3>Content Categories</h3>
                                    <div className="empty-state" style={{
                                        textAlign: 'center',
                                        padding: '2rem',
                                        color: '#666'
                                    }}>
                                        <p>No content categories available for this domain.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeSection === 'brand' && (
                        <div className="section brand-section">
                            {/* All Logo Variants */}
                            {brandData?.logos && brandData.logos.length > 0 && (
                                <div className="info-card">
                                    <h3>Brand Logos</h3>
                                    <div className="logo-variants">
                                        {brandData.logos.map((logoSet, index) => (
                                            <div key={index} className="logo-variant-container">
                                                <h4>{logoSet.type.charAt(0).toUpperCase() + logoSet.type.slice(1)} ({logoSet.theme})</h4>
                                                <div className={`logo-display ${logoSet.theme}`}>
                                                    {logoSet.formats && logoSet.formats[0] && (
                                                        <a 
                                                            href={logoSet.formats[0].src} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            title="Open full size logo"
                                                        >
                                                            <img src={logoSet.formats[0].src} alt={`${name || domain} ${logoSet.type} logo`} />
                                                        </a>
                                                    )}
                                                </div>
                                                <div className="logo-formats">
                                                    {logoSet.formats && logoSet.formats.map((format, fIdx) => (
                                                        <div key={fIdx} className="format-info">
                                                            <span>{format.format} {format.width && format.height ? `(${format.width}×${format.height})` : ''}</span>
                                                            <a 
                                                                href={format.src} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                className="format-link"
                                                            >
                                                                <ExternalLink size={16} />
                                                            </a>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Colors */}
                            {brandData?.colors && brandData.colors.length > 0 && (
                                <div className="info-card">
                                    <h3>Brand Colors</h3>
                                    <div className="color-grid">
                                        {brandData.colors.map((color, index) => (
                                            <div 
                                                key={index} 
                                                className={`color-item ${color.ai_generated ? 'ai-generated' : ''}`}
                                                onClick={() => {
                                                    navigator.clipboard.writeText(color.hex);
                                                    setCopiedColor(color.hex);
                                                    setTimeout(() => setCopiedColor(null), 2000);
                                                }}
                                            >
                                                <div 
                                                    className="color-preview" 
                                                    style={{ backgroundColor: color.hex }}
                                                >
                                                    {copiedColor === color.hex ? (
                                                        <Check className="copy-icon" />
                                                    ) : (
                                                        <Copy className="copy-icon" />
                                                    )}
                                                </div>
                                                <div className="color-info">
                                                    <span className="color-type">
                                                        {color.type}
                                                        {color.ai_generated && <span className="ai-badge" title="AI-suggested color">AI</span>}
                                                    </span>
                                                    <span className="color-hex">{color.hex}</span>
                                                    {color.rgb && (
                                                        <span className="color-rgb">RGB: {color.rgb.r}, {color.rgb.g}, {color.rgb.b}</span>
                                                    )}
                                                    {color.scheme_name && (
                                                        <span className="color-scheme">{color.scheme_name}</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Fonts */}
                            {brandData?.fonts && (
                                <div className="info-card">
                                    <h3>Brand Fonts</h3>
                                    <div className="font-grid">
                                        {brandData.fonts.map((font, index) => (
                                            <div key={index} className="font-item">
                                                <h4>{font.name} {font.weight ? `(${font.weight})` : ''}</h4>
                                                <div className="font-details">
                                                    <span className="font-type">{font.type}</span>
                                                    {font.format && <span className="font-format">{font.format}</span>}
                                                </div>
                                                <div className="font-preview">
                                                    <span style={{ fontFamily: font.name }}>
                                                        The quick brown fox jumps over the lazy dog
                                                    </span>
                                                </div>
                                                {font.url && (
                                                    <a 
                                                        href={font.url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="font-link"
                                                    >
                                                        <ExternalLink size={16} /> View Font
                                                    </a>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeSection === 'insights' && (
                        <div className="section insights-section">
                            {insights && (
                                <div className="info-card">
                                    <h3>AI Insights</h3>
                                    <ul className="insights-list">
                                        {Array.isArray(insights) 
                                            ? insights.map((insight, index) => (
                                                <li key={index}>{insight}</li>
                                            ))
                                            : typeof insights === 'object' && Object.keys(insights).length > 0
                                              ? Object.entries(insights).map(([key, value]) => (
                                                  <div key={key} className="insight-item">
                                                      <h4>{key.replace(/_/g, ' ').toUpperCase()}</h4>
                                                      <p>{value}</p>
                                                  </div>
                                                ))
                                              : <li>No insights available</li>
                                        }
                                    </ul>
                                </div>
                            )}
                            {recommendations && Array.isArray(recommendations) && (
                                <div className="info-card">
                                    <h3>Recommendations</h3>
                                    <ul className="recommendations-list">
                                        {recommendations.length > 0 
                                            ? recommendations.map((rec, index) => (
                                                <li key={index}>{rec}</li>
                                            ))
                                            : <li>No recommendations available</li>
                                        }
                                    </ul>
                                </div>
                            )}
                            
                            {/* Display App Ideas if available */}
                            {appIdeas && appIdeas.length > 0 && (
                                <div className="info-card">
                                    <h3>App Ideas</h3>
                                    <div className="app-ideas-list">
                                        {appIdeas.map((idea, index) => (
                                            <div key={index} className="app-idea-item">
                                                <h4>{idea.headline || `App Idea ${index + 1}`}</h4>
                                                <p>{idea.description}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Features */}
                            {features && Array.isArray(features) && features.length > 0 && (
                                <div className="info-card">
                                    <h3>Recommended Features</h3>
                                    <ul className="features-list">
                                        {features.map((feature, index) => {
                                            // Ensure feature is workable
                                            let processedFeature = feature;
                                            
                                            // If it's just a string, create a basic object
                                            if (typeof feature === 'string') {
                                                processedFeature = {
                                                    name: feature,
                                                    description: ''
                                                };
                                            } 
                                            // Safety check to ensure the feature is an object
                                            else if (!feature || typeof feature !== 'object') {
                                                processedFeature = {
                                                    name: 'Feature',
                                                    description: ''
                                                };
                                            }
                                            
                                            // Now ensure all properties are strings
                                            const safeName = ensureString(processedFeature.name || 'Feature');
                                            const safeDescription = ensureString(processedFeature.description || '');
                                            
                                            return (
                                                <li key={index} className="feature-item">
                                                    <h4 className="feature-name">{safeName}</h4>
                                                    <p className="feature-description">{safeDescription}</p>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {activeSection === 'media' && (
                        <div className="section media-section">
                            {/* Screenshots and Images */}
                            {screenshots && Object.keys(screenshots).length > 0 && (
                                <>
                                    {Object.entries(screenshots).map(([type, images]) => (
                                        <div key={type} className="info-card">
                                            <h3>{type.charAt(0).toUpperCase() + type.slice(1)}</h3>
                                            <div className="screenshots-grid">
                                                {images.map((image, index) => (
                                                    <div key={index} className="screenshot-item">
                                                        <a 
                                                            href={image.url} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            title="View full size"
                                                        >
                                                            <img 
                                                                src={image.url} 
                                                                alt={`${name || domain} ${type}`}
                                                                loading="lazy"
                                                            />
                                                        </a>
                                                        {(image.width && image.height) && (
                                                            <span className="image-dimensions">{image.width} × {image.height}</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                            
                            {(!screenshots || Object.keys(screenshots).length === 0) && (
                                <div className="info-card">
                                    <div className="empty-state">
                                        <Camera size={48} opacity={0.5} />
                                        <p>No media available for this brand</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {activeSection === 'links' && (
                        <div className="section links-section">
                            {/* Social Media Links */}
                            {((socialLinksWithOG && socialLinksWithOG.length > 0) || (socialLinks && socialLinks.length > 0)) && (
                                <div className="info-card">
                                    <h3 className="text-xl font-semibold mb-4">Social Media</h3>
                                    <div className="social-links-grid grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {(socialLinksWithOG.length > 0 ? socialLinksWithOG : socialLinks).map((link, index) => {
                                            // Extract username/handle from URL if not provided
                                            let handle = link.username;
                                            if (!handle && link.url) {
                                                try {
                                                    const url = new URL(link.url);
                                                    const pathParts = url.pathname.split('/').filter(part => part);
                                                    if (pathParts.length > 0) {
                                                        handle = '@' + pathParts[pathParts.length - 1];
                                                    }
                                                } catch (e) {
                                                    // If URL parsing fails, just use the domain
                                                    handle = link.url.replace(/^https?:\/\//, '').split('/')[0];
                                                }
                                            }
                                            
                                            return (
                                                <a 
                                                    key={index}
                                                    href={link.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="social-link-item flex items-center p-4 rounded-lg hover:bg-opacity-90 transition-all duration-200 border border-gray-200 shadow-sm hover:shadow-md"
                                                    style={{ 
                                                        backgroundColor: 'white',
                                                        borderLeft: `5px solid ${getSocialColor(link.type)}`
                                                    }}
                                                >
                                                    <div 
                                                        className="social-icon flex-shrink-0 w-14 h-14 flex items-center justify-center rounded-full mr-4"
                                                        style={{ 
                                                            backgroundColor: getSocialColor(link.type),
                                                            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
                                                        }}
                                                    >
                                                        {getSocialIcon(link.type)}
                                                    </div>
                                                    <div className="social-info flex-1 min-w-0">
                                                        <span className="social-platform block text-gray-800 font-bold text-lg">{formatPlatformName(link.type)}</span>
                                                        {handle && <span className="social-username block text-gray-600 text-sm">{handle}</span>}
                                                        {link.og && link.og.title && <span className="social-og-title block text-gray-700 text-xs mt-1">{link.og.title}</span>}
                                                        {link.followers && <span className="social-followers block text-gray-500 text-xs mt-1">{formatFollowers(link.followers)} followers</span>}
                                                        {link.verified && <span className="social-verified inline-flex items-center text-xs mt-1 text-blue-600"><CheckCircle size={12} className="mr-1" /> Verified</span>}
                                                    </div>
                                                    <div 
                                                        className="external-link-icon w-8 h-8 flex items-center justify-center rounded-full ml-2 flex-shrink-0"
                                                        style={{ 
                                                            backgroundColor: getSocialColor(link.type),
                                                            opacity: 0.2
                                                        }}
                                                    >
                                                        <ExternalLink size={14} className="text-gray-800" />
                                                    </div>
                                                </a>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            
                            {/* App Store Links */}
                            {appLinks && appLinks.length > 0 && (
                                <div className="info-card">
                                    <h3>Mobile Apps</h3>
                                    <div className="app-links-grid">
                                        {appLinks.map((app, index) => (
                                            <a
                                                key={index}
                                                href={app.app_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="app-link-item"
                                            >
                                                <div className="app-icon">
                                                    {getAppIcon(app.platform)}
                                                </div>
                                                <div className="app-info">
                                                    <span className="app-name">{app.app_name || `${name} App`}</span>
                                                    <span className="app-platform">{formatPlatformName(app.platform)}</span>
                                                </div>
                                                <ExternalLink size={16} className="app-external-icon" />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {(!socialLinks || socialLinks.length === 0) && (!appLinks || appLinks.length === 0) && (
                                <div className="info-card">
                                    <div className="empty-state">
                                        <Globe size={48} opacity={0.5} />
                                        <p>No links available for this brand</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeSection === 'ideas' && (
                        <div className="section app-ideas-section">
                            {/* App Name Options */}
                            {appSuggestions && (
                                <div className="info-card app-name-options">
                                    <h3>App Name Options</h3>
                                    <div className="app-names-grid" style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                        gap: '1rem',
                                        marginBottom: '1.5rem'
                                    }}>
                                        {(appSuggestions.name_option_1 || appSuggestions.name1) && (
                                            <div className="app-name-option" style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                padding: '1rem',
                                                borderRadius: '8px',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                border: '1px solid rgba(0,0,0,0.05)'
                                            }}>
                                                <h4 style={{color: dominantColors[0] || '#333'}}>Option 1</h4>
                                                <p style={{fontSize: '1.2rem', fontWeight: 'bold'}}>{appSuggestions.name_option_1 || appSuggestions.name1}</p>
                                            </div>
                                        )}
                                        {(appSuggestions.name_option_2 || appSuggestions.name2) && (
                                            <div className="app-name-option" style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                padding: '1rem',
                                                borderRadius: '8px',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                border: '1px solid rgba(0,0,0,0.05)'
                                            }}>
                                                <h4 style={{color: dominantColors[0] || '#333'}}>Option 2</h4>
                                                <p style={{fontSize: '1.2rem', fontWeight: 'bold'}}>{appSuggestions.name_option_2 || appSuggestions.name2}</p>
                                            </div>
                                        )}
                                        {(appSuggestions.name_option_3 || appSuggestions.name3) && (
                                            <div className="app-name-option" style={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                padding: '1rem',
                                                borderRadius: '8px',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                border: '1px solid rgba(0,0,0,0.05)'
                                            }}>
                                                <h4 style={{color: dominantColors[0] || '#333'}}>Option 3</h4>
                                                <p style={{fontSize: '1.2rem', fontWeight: 'bold'}}>{appSuggestions.name_option_3 || appSuggestions.name3}</p>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* App Description */}
                                    {(appSuggestions.description || appSuggestions.targetAudience) && (
                                        <div className="app-description" style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                            padding: '1rem',
                                            borderRadius: '8px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                            marginBottom: '1.5rem'
                                        }}>
                                            <h4>App Description</h4>
                                            <p>{appSuggestions.description}</p>
                                        </div>
                                    )}
                                    
                                    {/* Target Audience */}
                                    {(appSuggestions.target_audience || appSuggestions.targetAudience) && (
                                        <div className="app-target-audience" style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                            padding: '1rem',
                                            borderRadius: '8px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                            marginBottom: '1.5rem'
                                        }}>
                                            <h4>Target Audience</h4>
                                            <p>{appSuggestions.target_audience || appSuggestions.targetAudience}</p>
                                        </div>
                                    )}
                                    
                                    {/* Monetization Strategy - Previously missing data point */}
                                    {(appSuggestions.monetization_strategy || appSuggestions.monetizationStrategy) && (
                                        <div className="app-monetization" style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                            padding: '1rem',
                                            borderRadius: '8px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                            marginBottom: '1.5rem'
                                        }}>
                                            <h4>Monetization Strategy</h4>
                                            <p>{appSuggestions.monetization_strategy || appSuggestions.monetizationStrategy}</p>
                                        </div>
                                    )}
                                    
                                    {/* Development Time - Previously missing data point */}
                                    {(appSuggestions.development_time || appSuggestions.developmentTime) && (
                                        <div className="app-development-time" style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                            padding: '1rem',
                                            borderRadius: '8px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                            marginBottom: '1.5rem'
                                        }}>
                                            <h4>Estimated Development Time</h4>
                                            <p>{appSuggestions.development_time || appSuggestions.developmentTime}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* Technical Requirements - Previously missing data point */}
                            {technicalRequirements && technicalRequirements.length > 0 && (
                                <div className="info-card technical-requirements">
                                    <h3>Technical Requirements</h3>
                                    <ul className="requirements-list" style={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                        padding: '1rem 1rem 1rem 2rem',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        listStyleType: 'disc'
                                    }}>
                                        {Array.isArray(technicalRequirements) ? (
                                            technicalRequirements.map((req, index) => (
                                                <li key={index} style={{margin: '0.5rem 0'}}>{req}</li>
                                            ))
                                        ) : typeof technicalRequirements === 'string' ? (
                                            <li>{technicalRequirements}</li>
                                        ) : null}
                                    </ul>
                                </div>
                            )}

                            {/* App Ideas */}
                            {appIdeas && Array.isArray(appIdeas) && appIdeas.length > 0 && (
                                <div className="info-card app-ideas-section">
                                    <h3>App Ideas</h3>
                                    <div className="app-ideas-list">
                                        {appIdeas.map((idea, index) => {
                                            // Ensure idea is workable
                                            let processedIdea = idea;
                                            
                                            // If it's just a string, create a basic object
                                            if (typeof idea === 'string') {
                                                processedIdea = {
                                                    headline: idea,
                                                    description: ''
                                                };
                                            } 
                                            // Safety check to ensure the idea is an object
                                            else if (!idea || typeof idea !== 'object') {
                                                processedIdea = {
                                                    headline: 'App Idea',
                                                    description: ''
                                                };
                                            }
                                            
                                            // Now ensure all properties are strings
                                            const safeHeadline = ensureString(processedIdea.headline || 'App Idea');
                                            const safeDescription = ensureString(processedIdea.description || '');
                                            
                                            return (
                                                <div key={index} className="app-idea-item">
                                                    <h4 className="app-idea-headline">{safeHeadline}</h4>
                                                    <p className="app-idea-description">{safeDescription}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeSection === 'marketing' && (
                        <div className="section marketing-section">
                            {/* Marketing Tips */}
                            {marketingTips && marketingTips.length > 0 ? (
                                <div className="info-card">
                                    <h3>Marketing Tips</h3>
                                    <ul className="marketing-tips-list">
                                        {marketingTips.map((tip, index) => {
                                            // First ensure tip is workable
                                            let processedTip = tip;
                                            
                                            // If it's just a string, create a basic object
                                            if (typeof tip === 'string') {
                                                processedTip = {
                                                    category: 'General',
                                                    text: tip
                                                };
                                            } 
                                            // Safety check to ensure the tip is an object
                                            else if (!tip || typeof tip !== 'object') {
                                                processedTip = {
                                                    category: '',
                                                    text: 'Unknown tip'
                                                };
                                            }
                                            
                                            // Now ensure all properties are strings
                                            const safeCategory = ensureString(processedTip.category || '');
                                            // Use tip_text if available, otherwise fall back to text property
                                            const safeText = ensureString(processedTip.tip_text || processedTip.text || '');
                                            
                                            return (
                                                <li key={index} className="marketing-tip-item">
                                                    {safeCategory && (
                                                        <span className="marketing-tip-category">{safeCategory}</span>
                                                    )}
                                                    <p className="marketing-tip-text">{safeText}</p>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ) : (
                                <div className="info-card">
                                    <h3>Marketing Tips</h3>
                                    <div className="empty-state">
                                        <p>No marketing tips available for this domain.</p>
                                    </div>
                                </div>
                            )}
                            
                            {/* Content Categories */}
                            {contentCategories && Array.isArray(contentCategories) && contentCategories.length > 0 && (
                                <div className="info-card">
                                    <h3>Recommended Content Categories</h3>
                                    <div className="content-categories-grid">
                                        {contentCategories.map((category, index) => {
                                            // Safety is now ensured when we set the state
                                            return (
                                                <div key={index} className="category-item">
                                                    <span className="category-name">{category.name}</span>
                                                    <span className="category-score">{category.score}/10</span>
                                                    <p className="category-description">{category.description}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Helper functions for social media icons
const getSocialIcon = (platform) => {
    // Return appropriate icon based on platform
    if (!platform) return <Globe size={24} className="text-white" />;
    
    switch (platform.toLowerCase()) {
        case 'twitter':
        case 'x':
            return <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path></svg>;
        case 'facebook':
            return <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"></path></svg>;
        case 'instagram':
            return <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"></path></svg>;
        case 'linkedin':
            return <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"></path></svg>;
        case 'youtube':
            return <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"></path></svg>;
        case 'pinterest':
            return <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"></path></svg>;
        case 'tiktok':
            return <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"></path></svg>;
        case 'snapchat':
            return <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.359-1.275.975-3.225 1.125-.046.193-.122.6-.122.6-.046.15-.06.404-.224.404-.15 0-.3-.044-.42-.044-.255 0-.54.045-1.006.135-.255.029-.54.044-.824.044-1.202 0-1.667-.404-2.302-.868-.69-.495-1.411-1.005-2.572-1.005-.061 0-.12 0-.18.015-.51.029-1.035.089-1.53.089-.54 0-1.036-.089-1.41-.269-.54-.179-1.036-.509-1.411-.93-.448-.465-1.182-1.305-1.182-2.593 0-.512.104-1.004.3-1.48.12-.285.254-.645.254-1.049 0-.225-.061-.436-.165-.615-.255-.448-.765-.733-1.291-.958-.436-.196-.826-.374-1.096-.643-.135-.136-.27-.3-.27-.584 0-.222.09-.416.255-.567.3-.196.674-.346 1.006-.346.194 0 .374.06.538.196.12.104.226.224.271.345.12.149.179.345.225.523.12.301.405.406.63.406.164 0 .299-.045.449-.105.436-.165.675-.45.675-.75 0-.149-.045-.27-.134-.42-.196-.346-.511-.645-.93-.869-.225-.134-.48-.224-.745-.224-.255 0-.509.044-.763.135-1.154.344-1.683 1.005-1.683 1.725 0 .509.224.958.584 1.355.195.225.449.39.723.584.046.03.091.044.135.075.3.149.659.344.66.706 0 .134-.06.284-.166.404-.135.165-.345.285-.583.285-.121 0-.226-.016-.331-.045-.135-.074-.239-.164-.344-.299-.09-.134-.194-.27-.269-.404-.225-.299-.54-.494-.96-.494-.134 0-.284.045-.403.09-.225.149-.36.42-.36.704 0 .586.525 1.094 1.215 1.094.135 0 .27-.014.405-.044.704-.165 1.305-.45 1.756-.884.239-.209.435-.42.645-.645.135.375.3.734.494 1.094.9 1.684 2.302 2.234 3.897 2.234.555 0 1.095-.044 1.605-.164.285-.045.57-.074.854-.074.314 0 .629.029.929.119.6.18 1.096.494 1.486.869.42.42 1.275.899 2.251.899.194 0 .389-.015.584-.03.705-.09 1.321-.284 1.771-.629.239-.18.42-.404.555-.659.134.015.27.015.404.015 1.155 0 2.55-.404 3.329-1.064.225-.194.435-.42.585-.659.104-.165.225-.39.225-.585 0-.404-.255-.719-.63-.838z"></path></svg>;
        case 'github':
            return <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"></path></svg>;
        case 'medium':
            return <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zM20.96 12c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z"></path></svg>;
        case 'reddit':
            return <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"></path></svg>;
        case 'website':
            return <Globe size={24} className="text-white" />;
        default:
            return <Globe size={24} className="text-white" />;
    }
};

// Get social media brand colors
const getSocialColor = (platform) => {
    if (!platform) return '#1e293b';
    
    switch (platform.toLowerCase()) {
        case 'twitter':
        case 'x':
            return '#1DA1F2';
        case 'facebook':
            return '#1877F2';
        case 'instagram':
            return '#E4405F';
        case 'linkedin':
            return '#0A66C2';
        case 'youtube':
            return '#FF0000';
        case 'pinterest':
            return '#BD081C';
        case 'tiktok':
            return '#000000';
        case 'snapchat':
            return '#FFFC00';
        case 'github':
            return '#181717';
        case 'medium':
            return '#00AB6C';
        case 'reddit':
            return '#FF4500';
        case 'dribbble':
            return '#EA4C89';
        case 'behance':
            return '#1769FF';
        case 'website':
            return '#0056B3';
        default:
            return '#1e293b';
    }
};

// Helper function to format platform names
const formatPlatformName = (platform) => {
    if (!platform) return 'Website';
    
    // Special cases
    if (platform.toLowerCase() === 'x') return 'Twitter (X)';
    
    // Capitalize first letter of each word
    return platform
        .split('_')
        .join(' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

// Helper function to format follower counts
const formatFollowers = (count) => {
    if (!count) return '0';
    
    if (count >= 1000000) {
        return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
        return `${(count / 1000).toFixed(1)}K`;
    } else {
        return count.toString();
    }
};

// Helper function for app icons
const getAppIcon = (platform) => {
    if (!platform) return <Globe size={20} className="text-white" />;
    
    switch (platform.toLowerCase()) {
        case 'ios':
        case 'apple':
        case 'app_store':
            return <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.085 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"></path></svg>;
        case 'android':
        case 'play_store':
            return <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.523 15.3414l-.0001-.0002-.0002-.0002-.0012-.0012-.0097-.0097c-.0639-.0644-.1746-.0947-.2905-.0589l-1.6108.5a4.0043 4.0043 0 0 1-2.4944 0l-1.6108-.5c-.1132-.035-.2298-.0102-.292.0582l-.0095.0095a.2954.2954 0 0 0-.0582.092c-.035.1131.0103.2296.0583.292l.5 1.6108a3.9556 3.9556 0 0 0 5.1892-.0001l.5-1.6108c.035-.1159.0055-.2266-.0583-.2905zM6.6268 9.3176a.825.825 0 0 1 .2893-1.131.8252.8252 0 0 1 1.131.2892 6.7997 6.7997 0 0 0 11.9059 0 .825.825 0 0 1 1.131-.2892.8251.8251 0 0 1 .2892 1.131 8.4502 8.4502 0 0 1-14.7455 0zM12 6.5678a.825.825 0 0 1-.825-.825V.825a.825.825 0 0 1 1.65 0v4.9178c0 .4557-.3693.825-.825.825zm0 10.8644a.825.825 0 0 1-.825-.825v-4.9177a.825.825 0 0 1 1.65 0v4.9177c0 .4558-.3693.825-.825.825zm5.8248-10.8644a.825.825 0 0 1-.825-.825V.825a.825.825 0 0 1 1.65 0v4.9178c0 .4557-.3693.825-.825.825zm0 10.8644a.825.825 0 0 1-.825-.825v-4.9177a.825.825 0 0 1 1.65 0v4.9177c0 .4558-.3693.825-.825.825zM6.1752 6.5678a.825.825 0 0 1-.825-.825V.825a.825.825 0 0 1 1.65 0v4.9178c0 .4557-.3693.825-.825.825zm0 10.8644a.825.825 0 0 1-.825-.825v-4.9177a.825.825 0 0 1 1.65 0v4.9177c0 .4558-.3693.825-.825.825z"></path></svg>;
        default:
            return <Smartphone size={20} className="text-white" />;
    }
};

export default BrandfetchDisplay; 